// engine.alias.ts
// Alias Resolution Engine for Tempo
// Responsible for event/period alias mapping, collision detection, and snippet rebinding
import { isDefined, isFunction } from '#library/assertion.library.js';
import { Match, logError, logWarn } from '#tempo/support';
import { ownEntries } from '#library/primitive.library.js';
export class AliasEngine {
    static aliasPattern = /^(evt|per)(\d+)_(\d+)$/;
    static _idCounter = 0;
    static _getBaseWord(s) {
        return s
            .toLowerCase()
            .replace(/\[[^\]]*\]\?/g, '')
            .replace(/.\?/g, '')
            .replace(/[^a-z0-9]/g, '');
    }
    #parent;
    #config;
    #depth; // the depth of this engine in the proto chain
    #count; // count of aliases registered at this level (used for indexing)														
    #state; // object that holds alias mappings, collisions, and registry for this engine 
    #words; // object of base words for collision detection
    #id;
    #version = 0;
    get depth() {
        return this.#depth;
    }
    get id() { return this.#id; }
    get parent() { return this.#parent; }
    getWords() { return this.#words; }
    constructor(options = {}) {
        const parent = options.parent;
        this.#config = options.config;
        this.#id = AliasEngine._idCounter++;
        if (parent instanceof AliasEngine) {
            this.#parent = parent;
            this.#depth = parent.depth + 1;
            this.#state = Object.create(parent.#state); // create a new state object that inherits from the parent engine's state
            this.#words = Object.create(parent.#words); // create a new words object that inherits from the parent engine's words for collision detection
        }
        else {
            if (parent)
                logError("Parent engine must be an instance of AliasEngine", this.#config);
            this.#parent = null;
            this.#depth = 0;
            this.#state = Object.create(null); // initialize an empty state for the root engine (no parent)
            this.#words = Object.create(null); // initialize an empty words object for the root engine (no parent)
        }
        this.#count = { evt: 0, per: 0 };
    }
    fork(config) {
        return new AliasEngine({ parent: this, config: config ?? this.#config ?? null });
    }
    /**
     * Register aliases and return a regex string representing the full lineage of aliases up the proto chain.
     * Ensures that shadowed/collided baseNames are excluded from parent levels.
     * Note that we track collisions across both Event and Period aliases in the same #words object, since they can
     * collide with each other (e.g. "on" could be an event alias and a period alias,
     * which would cause confusion and unintended matches).
     */
    registerAliases(type, events) {
        let mutated = false;
        for (const [name, target] of events) {
            const baseWord = AliasEngine._getBaseWord(name);
            const existingKey = this.#words[baseWord];
            const existing = existingKey ? this.getAlias(existingKey) : undefined;
            // Skip identical re-registrations across the entire prototype chain to avoid redundant warnings and state growth
            if (this.getAliases(type, true).some(a => a.target === target && a.name === name))
                continue;
            mutated = true;
            const index = (this.#count[type]++);
            const aliasKey = `${type}${this.#depth}_${index}`;
            const shouldOverwrite = !(existing?.type === 'evt' && type === 'per');
            if (baseWord in this.#words) {
                logWarn(`[AliasEngine] Collision detected for ${type} alias "${name}". ${shouldOverwrite ? 'Overwriting' : 'Preserving'} existing alias.`, this.#config);
            }
            if (shouldOverwrite)
                this.#words[baseWord] = aliasKey;
            this.#state[aliasKey] = {
                name, // plain string or regex-like string
                target, // string, number, or function
                type, // 'evt' or 'per'
                baseWord, // used for collision detection
                collision: isDefined(existingKey),
                depth: this.#depth,
            };
        }
        if (mutated)
            this.#version++;
        return this.getPatterns(type);
    }
    /**
     * Build regex patterns for this engine and all parent engines, excluding shadowed/collided baseNames.
     * This ensures that if an alias is shadowed by a child engine,
     * it won't be included in the regex patterns of the parent engine,
     * preventing unintended matches and preserving the expected behavior of alias resolution.
     */
    getPatterns(type, seenBaseNames = new Set(), winnerLookup) {
        const patterns = [];
        const winners = winnerLookup ?? this.#words;
        for (const [alias, register] of ownEntries(this.#state)) {
            if (register.type === type && !seenBaseNames.has(register.baseWord)) {
                // Check for cross-type collision priority (Events win over Periods)
                if (type === 'per') {
                    const winnerKey = winners[register.baseWord];
                    const winner = this.getAlias(winnerKey);
                    if (winner && winner.type === 'evt') {
                        continue; // Skip period if an event is using the same baseWord
                    }
                }
                seenBaseNames.add(register.baseWord);
                patterns.push(`(?<${alias}>${Match.safeAlias(register.name)})`);
            }
        }
        if (this.#parent) {
            const parentPatterns = this.#parent.getPatterns(type, seenBaseNames, winners);
            if (parentPatterns)
                patterns.push(parentPatterns);
        }
        return patterns.join('|');
    }
    getVersion() {
        return this.#version + (this.#parent?.getVersion() ?? 0);
    }
    hasAlias(name, type) {
        const baseWord = AliasEngine._getBaseWord(name);
        const key = this.#words[baseWord];
        return !key
            ? false
            : type
                ? this.#state[key].type === type
                : true;
    }
    resolveAlias(name, thisArg) {
        const register = this.getAlias(name);
        if (!register)
            return undefined;
        let value = '';
        const isFn = isFunction(register.target);
        if (isFunction(register.target)) {
            const result = register.target.call(thisArg);
            value = isDefined(result) ? result.toString() : '';
        }
        else {
            value = register.target.toString();
        }
        return {
            value,
            key: register.baseWord,
            type: register.type,
            source: register.depth === 0 ? 'global' : 'local',
            isClock: Match.clock.test(value),
            isFunction: isFn
        };
    }
    getAlias(key) {
        return this.#state[key] ?? this.#parent?.getAlias(key);
    }
    getAliases(type, recurse = false) {
        const aliases = [];
        ownEntries(this.#state)
            .filter(([_, register]) => !type || register.type === type)
            .forEach(([key, register]) => {
            aliases.push(Object.assign({}, { key }, register));
        });
        if (recurse && this.#parent)
            aliases.push(...this.#parent.getAliases(type, true));
        return aliases;
    }
    clear(type) {
        this.#count[type] = 0;
        for (const registry in this.#state) {
            if (this.#state[registry].type === type) {
                delete this.#state[registry];
            }
        }
        // Rebuild #words and re-calculate counts from remaining state
        this.#words = Object.create(this.#parent ? this.#parent.#words : null);
        this.#count = { evt: 0, per: 0 };
        for (const [key, register] of ownEntries(this.#state)) {
            this.#words[register.baseWord] = key;
            this.#count[register.type]++;
        }
        this.#version++;
    }
}
